// src/plugins/currencyConverter.js

import axios from "axios";

const isBrowser = typeof window !== "undefined";
const RATES_STORAGE_KEY = "appCurrencyRates";
const DEFAULT_BASE_CURRENCY = (process.env.VUE_APP_BASE_CURRENCY || "EUR").toUpperCase();

const readStoredRates = () => {
  if (!isBrowser) {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(RATES_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.rates) {
      return null;
    }
    parsed.base = (parsed.base || "USD").toUpperCase();
    const normalisedRates = {};
    Object.entries(parsed.rates).forEach(([code, value]) => {
      const numeric = Number(value);
      if (Number.isFinite(numeric)) {
        normalisedRates[code.toUpperCase()] = numeric;
      }
    });
    return {
      rates: normalisedRates,
      base: parsed.base,
      lastFetched: parsed.lastFetched || null,
    };
  } catch (error) {
    console.warn("Unable to load cached currency rates", error);
    return null;
  }
};

const persistRates = (data) => {
  if (!isBrowser || !data) {
    return;
  }
  try {
    window.localStorage.setItem(
      RATES_STORAGE_KEY,
      JSON.stringify({
        rates: data.rates,
        base: data.base,
        lastFetched: data.lastFetched,
      })
    );
  } catch (error) {
    console.warn("Unable to persist currency rates", error);
  }
};

const cachedFromStorage = readStoredRates();

const ratesCache = {
  rates: cachedFromStorage?.rates || null,
  base: cachedFromStorage?.base || "USD",
  lastFetched: cachedFromStorage?.lastFetched || null,
};

const MONEYCONVERT_ENDPOINT = "https://cdn.moneyconvert.net/api/latest.json";
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

const scheduleCurrencyRateRefresh = () => {
  const now = new Date();
  const nextMidnight = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() + 1, // tomorrow's date
    0, 0, 0, 0         // at 00:00:00
  );
  // const nextMidnight = new Date(
  //   now.getFullYear(),
  //   now.getMonth(),
  //   now.getDate(), // tomorrow's date
  //   14, 54, 0, 0         // at 02:54 pm (for testing)
  // );

  const msUntilMidnight = nextMidnight.getTime() - now.getTime();

  // Wait until next midnight, then fetch and set up daily interval
  setTimeout(() => {
    fetchRates(true); // Fetch immediately at midnight

    setInterval(() => {
      fetchRates(true); // Then fetch every 24 hours
    }, ONE_DAY_MS);
  }, msUntilMidnight);
};

const getTodayDateString = () => {
  const today = new Date();
  return today.toISOString().split('T')[0]; // "YYYY-MM-DD"
};

const fetchRates = async (forceUpdate = false) => {
  const todayDate = getTodayDateString();
  if (forceUpdate || !ratesCache.rates || !ratesCache.lastFetched || ratesCache.lastFetched !== todayDate) {
    try {
      console.log("Fetching currency rates...");
      const response = await axios.get(MONEYCONVERT_ENDPOINT);
      const responseRates = response?.data?.rates || {};
      const normalisedRates = {};
      Object.entries(responseRates).forEach(([code, value]) => {
        const numeric = Number(value);
        if (Number.isFinite(numeric)) {
          normalisedRates[code.toUpperCase()] = numeric;
        }
      });
      ratesCache.rates = normalisedRates;
      ratesCache.base = (response?.data?.base || "USD").toUpperCase();
      ratesCache.lastFetched = todayDate;
      persistRates({
        rates: normalisedRates,
        base: ratesCache.base,
        lastFetched: todayDate,
      });
    } catch (err) {
      console.error("Failed to fetch currency rates:", err);
      if (!ratesCache.rates) {
        const stored = readStoredRates();
        if (stored) {
          ratesCache.rates = stored.rates;
          ratesCache.base = stored.base;
          ratesCache.lastFetched = stored.lastFetched;
        }
      }
    }
  }
};

const normaliseCurrencyCode = (code, fallback) => {
  const target = code || fallback;
  if (!target || typeof target !== "string") {
    return undefined;
  }
  return target.trim().toUpperCase();
};

const convertAmount = (amount, fromCurrency, toCurrency) => {
  const numericAmount = Number(amount);
  if (!Number.isFinite(numericAmount)) {
    return 0;
  }

  const fromCode = normaliseCurrencyCode(fromCurrency, DEFAULT_BASE_CURRENCY);
  const toCode = normaliseCurrencyCode(toCurrency, fromCode);

  if (!fromCode || !toCode || fromCode === toCode) {
    return numericAmount;
  }

  if (!ratesCache.rates) {
    console.warn("Currency rates not available; returning unconverted amount.");
    return numericAmount;
  }

  const fromRate = fromCode === ratesCache.base ? 1 : ratesCache.rates?.[fromCode];
  const toRate = toCode === ratesCache.base ? 1 : ratesCache.rates?.[toCode];

  if ((fromCode !== ratesCache.base && !fromRate) || (toCode !== ratesCache.base && !toRate)) {
    console.warn(`Missing exchange rate for ${fromCode} or ${toCode}.`);
    return numericAmount;
  }

  let amountInBase = numericAmount;
  if (fromCode !== ratesCache.base) {
    amountInBase = numericAmount / fromRate;
  }

  if (toCode === ratesCache.base) {
    return amountInBase;
  }

  return amountInBase * toRate;
};

export default {
  install: (app) => {
    const getStore = () => app.config.globalProperties.$store ?? null;
    const getStoreState = () => getStore()?.state ?? {};

    const getFormattedCurrency = (amount, targetCurrency = "USD", localeOverride) => {
      const appState = getStoreState()?.app ?? {};
      const locale = localeOverride || appState.locale || "en-US";
      const currencyCode = normaliseCurrencyCode(targetCurrency, DEFAULT_BASE_CURRENCY) || DEFAULT_BASE_CURRENCY;
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: currencyCode,
      }).format(amount ?? 0);
    };

    const getBaseCurrency = () => {
      const base = getStoreState()?.app?.baseCurrency || DEFAULT_BASE_CURRENCY;
      return normaliseCurrencyCode(base, DEFAULT_BASE_CURRENCY) || DEFAULT_BASE_CURRENCY;
    };

    const getActiveCurrency = () => {
      const active = getStoreState()?.app?.currency || DEFAULT_BASE_CURRENCY;
      return normaliseCurrencyCode(active, DEFAULT_BASE_CURRENCY) || DEFAULT_BASE_CURRENCY;
    };

    app.config.globalProperties.$convertCurrency = (
      amount,
      targetCurrency = getActiveCurrency(),
      options = {}
    ) => {
      if (amount == null) {
        return getFormattedCurrency(0, targetCurrency);
      }

      const fromCurrency = normaliseCurrencyCode(
        options.fromCurrency,
        getBaseCurrency()
      );
      const targetCode = normaliseCurrencyCode(targetCurrency, getActiveCurrency());
      const converted = convertAmount(amount, fromCurrency, targetCode);
      if (options.format === false) {
        return converted;
      }
      return getFormattedCurrency(converted, targetCode, options.locale);
    };

    app.config.globalProperties.$convertCurrencyRaw = (
      amount,
      targetCurrency = getActiveCurrency(),
      options = {}
    ) => {
      const fromCurrency = normaliseCurrencyCode(
        options.fromCurrency,
        getBaseCurrency()
      );
      const targetCode = normaliseCurrencyCode(targetCurrency, getActiveCurrency());
      return convertAmount(amount, fromCurrency, targetCode);
    };
  }
};

export { fetchRates, scheduleCurrencyRateRefresh };
